<template>
  <div class="inbound-cert-queue">
    <div class="panel-header">
      <h4>Certificate email intake</h4>
      <p v-if="inboundAddress" class="panel-subtitle">
        Base intake: <strong>{{ inboundAddress }}</strong>.
        Give agents a plus-tag per store (Gmail-style), e.g.
        <strong>{{ exampleStoreAddress }}</strong>, so Epic/CSR24 sends land on the right location without a shared JMFS inbox.
        Unmatched messages appear here for entity confirmation.
      </p>
      <p v-if="plusTagHelp" class="panel-subtitle muted">{{ plusTagHelp }}</p>
      <p v-else class="panel-subtitle">
        Set a project intake address in admin to enable forwarding.
      </p>
    </div>

    <div v-if="loading" class="loading-row">Loading queue…</div>
    <div v-else-if="!items.length" class="empty-row">No messages waiting for review.</div>
    <ul v-else class="queue-list">
      <li v-for="item in items" :key="item.id" class="queue-item">
        <div class="queue-meta">
          <span class="queue-subject">{{ item.subject_line || '(no subject)' }}</span>
          <span class="queue-from">{{ item.original_sender_email || 'unknown sender' }}</span>
          <span v-if="item.attachment_name" class="queue-file">{{ item.attachment_name }}</span>
        </div>
        <div v-if="item.match_payload?.candidates?.length" class="candidates">
          <label :for="`subject-${item.id}`">Match to entity</label>
          <select :id="`subject-${item.id}`" v-model="selectedSubject[item.id]">
            <option :value="null" disabled>Select subject</option>
            <option
              v-for="c in item.match_payload.candidates"
              :key="c.subject_id"
              :value="c.subject_id"
            >
              {{ c.subject_name || c.entity_name }} ({{ Math.round((c.confidence || 0) * 100) }}%)
            </option>
          </select>
        </div>
        <div v-else class="candidates muted">No match candidates — pick subject ID manually in Agent chat if needed.</div>
        <div class="queue-actions">
          <input
            v-if="!item.match_payload?.candidates?.length"
            v-model="manualSubject[item.id]"
            type="number"
            placeholder="Compliance subject ID"
            class="manual-id"
          />
          <button
            type="button"
            class="btn-primary"
            :disabled="confirming[item.id]"
            @click="confirmItem(item)"
          >
            {{ confirming[item.id] ? 'Running check…' : 'Confirm & run check' }}
          </button>
        </div>
        <p v-if="errors[item.id]" class="error-text">{{ errors[item.id] }}</p>
      </li>
    </ul>
  </div>
</template>

<script>
import api from '@/services/api';

export default {
  name: 'InboundCertificateQueue',
  props: {
    projectId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      inboundAddress: '',
      exampleStoreAddress: '',
      plusTagHelp: '',
      items: [],
      selectedSubject: {},
      manualSubject: {},
      confirming: {},
      errors: {},
    };
  },
  mounted() {
    this.loadQueue();
  },
  methods: {
    async loadQueue() {
      this.loading = true;
      try {
        const { data } = await api.get('/compliance-health/inbound-certificates/', {
          params: { project_id: this.projectId, status: 'NEEDS_REVIEW' },
        });
        this.inboundAddress = data.inbound_address || '';
        this.exampleStoreAddress = data.tagged_address_examples?.store || '';
        this.plusTagHelp = data.plus_tag_help || '';
        this.items = data.results || [];
        this.items.forEach((item) => {
          const top = item.match_payload?.candidates?.[0];
          if (top?.subject_id) {
            this.selectedSubject[item.id] = top.subject_id;
          }
        });
      } catch (e) {
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    async confirmItem(item) {
      const subjectId =
        this.selectedSubject[item.id] ||
        (this.manualSubject[item.id] ? Number(this.manualSubject[item.id]) : null);
      if (!subjectId) {
        this.errors = { ...this.errors, [item.id]: 'Select or enter a compliance subject.' };
        return;
      }
      this.confirming = { ...this.confirming, [item.id]: true };
      this.errors = { ...this.errors, [item.id]: '' };
      try {
        await api.post(`/compliance-health/inbound-certificates/${item.id}/confirm/`, {
          compliance_subject_id: subjectId,
        });
        await this.loadQueue();
      } catch (e) {
        const msg = e.response?.data?.error || e.message || 'Confirm failed';
        this.errors = { ...this.errors, [item.id]: msg };
      } finally {
        this.confirming = { ...this.confirming, [item.id]: false };
      }
    },
  },
};
</script>

<style scoped>
.inbound-cert-queue {
  margin-top: 1.5rem;
  padding: 1rem;
  border: 1px solid var(--border-subtle, #e5e7eb);
  border-radius: 8px;
}
.panel-subtitle {
  font-size: 0.875rem;
  color: var(--text-muted, #6b7280);
  margin: 0.25rem 0 1rem;
}
.queue-list {
  list-style: none;
  padding: 0;
  margin: 0;
}
.queue-item {
  padding: 0.75rem 0;
  border-top: 1px solid var(--border-subtle, #e5e7eb);
}
.queue-meta {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  margin-bottom: 0.5rem;
}
.queue-subject {
  font-weight: 600;
}
.queue-from,
.queue-file {
  font-size: 0.8125rem;
  color: var(--text-muted, #6b7280);
}
.candidates select,
.manual-id {
  margin: 0.5rem 0;
  max-width: 100%;
}
.queue-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
}
.error-text {
  color: #b91c1c;
  font-size: 0.8125rem;
}
.muted {
  font-size: 0.8125rem;
  color: var(--text-muted, #6b7280);
}
</style>
